// Super Admin schemas (architecture.md §24 M10, §25). Every admin action is audited.
import { z } from 'zod';
import { uuid, timezone, locale, username } from './common.js';

// Empty strings from a settings form mean "clear this field" → coerce to null.
const emptyToNull = (v) => (typeof v === 'string' && v.trim() === '' ? null : v);

export const changeUserPlanSchema = z.object({
  plan_id: uuid,
  // Plan validity in days; 0 or omitted = permanent (no expiry). Ignored for the Free plan.
  valid_days: z.coerce.number().int().min(0).max(3650).optional(),
});

// Self-service profile update (PATCH /me) and admin profile edits. Username is the public
// booking-link handle (citext-unique — a clash returns 409); phone is free-form contact text.
export const updateUserProfileSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  username: username.optional(),
  brand_name: z.preprocess(emptyToNull, z.string().trim().max(120).optional().nullable()),
  phone: z.preprocess(emptyToNull, z.string().trim().max(32).optional().nullable()),
  timezone: timezone.optional(),
  locale: locale.optional(),
});

export const platformSettingsSchema = z.object({
  site_title: z.string().trim().min(1).max(120).optional().nullable(),
  default_locale: locale.optional(),
  // SMTP (outbound email). Password is write-only: sent here to be encrypted at rest,
  // never returned by the read endpoint (which exposes only `smtp_password_set`).
  smtp_host: z.preprocess(emptyToNull, z.string().trim().max(255).nullable().optional()),
  smtp_port: z.preprocess(
    (v) => (v === '' || v == null ? null : v),
    z.coerce.number().int().min(1).max(65535).nullable().optional(),
  ),
  smtp_user: z.preprocess(emptyToNull, z.string().trim().max(255).nullable().optional()),
  smtp_password: z.preprocess(emptyToNull, z.string().max(512).nullable().optional()),
  // Manual payment config (public — shown on the Upgrade Plan page). QR image is set via
  // the dedicated upload endpoint, but may be cleared here.
  upi_id: z.preprocess(emptyToNull, z.string().trim().max(120).nullable().optional()),
  bank_details: z.preprocess(emptyToNull, z.string().trim().max(2000).nullable().optional()),
  payment_qr_url: z.preprocess(emptyToNull, z.string().trim().max(1000).nullable().optional()),
  whatsapp_number: z.preprocess(emptyToNull, z.string().trim().max(32).nullable().optional()),
  // Lifetime deal (manual UPI). QR image is set via its own upload endpoint; may be cleared here.
  lifetime_deal_enabled: z.boolean().optional(),
  lifetime_deal_price: z.preprocess(emptyToNull, z.string().trim().max(60).nullable().optional()),
  lifetime_deal_qr_url: z.preprocess(emptyToNull, z.string().trim().max(1000).nullable().optional()),
});

// Legal / info page content edit (§24 M10). `content` is a per-locale override object
// ({ en: {...}, bn: {...} }) that mirrors the page's COPY — kept permissive on purpose so
// the shape can evolve with the pages without a schema change; the slug is whitelisted
// server-side. Capped to a sane size to prevent abuse.
export const updateLegalPageSchema = z.object({
  content: z.record(z.any()).refine(
    (c) => JSON.stringify(c).length <= 200_000,
    'Content is too large',
  ),
});

// Manual booking email send from the Global Appointments log (§18).
export const adminSendEmailSchema = z.object({
  template: z.enum(['booking_confirmed', 'booking_cancelled', 'booking_reminder']),
});

// Pagination for admin listings. `action` is an optional exact-match filter used by the
// audit log (ignored by the other listings that share this schema).
export const adminListQuerySchema = z.object({
  q: z.string().trim().max(160).optional(),
  action: z.string().trim().max(80).optional(),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(100).default(25),
});
